import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SafeImage from "./SafeImage";
import { useLanguage } from "../../contexts/LanguageContext";

const products = [
  {
    name: "Mini",
    size: '4.2"',
    image: "/images/products/kylendar-mini.webp",
    alt: "Mini 4.2-inch Kylendar e-paper calendar on a study desk",
    imageClass: "bg-[#f4f1ea]",
    bestFor: "Best for small spaces",
    details: ["Bedside", "Entryway", "Compact desks"],
  },
  {
    name: "Standard",
    size: '7.5"',
    image: "/images/products/kylendar-standard.webp",
    alt: "Standard 7.5-inch Kylendar e-paper calendar on an executive desk",
    imageClass: "bg-[#1c1f1d]",
    bestFor: "Best for daily planning",
    details: ["Work desk", "Home office", "Meeting room"],
  },
];

export default function ProductSeriesSection() {
  const { t } = useLanguage();
  const copy = t("kylendar.series");

  return (
    <section id="product-series" className="home-wash relative border-y border-brand-100 bg-brand-surface/70 py-20 sm:py-24">
      <span className="home-orb left-1/4 top-0 h-48 w-48 bg-brand-200/50" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <div className="home-section-mark mx-auto max-w-2xl text-center">
          <p className="home-eyebrow">{copy.eyebrow}</p>
          <h2 className="home-title mt-3">{copy.title}</h2>
        </div>

        <div className="mx-auto mt-12 grid max-w-5xl gap-6 lg:grid-cols-2">
          {products.map((product, index) => {
            const [bestFor, details, viewLabel] = copy.products[index];
            return (
            <article
              key={product.name}
              className="group overflow-hidden rounded-3xl border border-white/70 bg-white shadow-[0_24px_50px_-36px_rgba(15,23,42,.5)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-32px_rgba(14,181,126,.35)]"
            >
              <div className={`aspect-[4/3] w-full p-4 sm:p-6 ${product.imageClass}`}>
                <SafeImage
                  src={product.image}
                  alt={product.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                />
              </div>
              <div className="p-6 sm:p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-700">{bestFor}</p>
                <h3 className="mt-2 font-display text-2xl font-bold tracking-[-0.03em] text-slate-950">
                  {product.name} {product.size}
                </h3>
                <ul className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600">
                  {details.map((detail) => (
                    <li key={detail} className="rounded-md bg-slate-50 px-2.5 py-1.5">
                      {detail}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/products"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand-700 transition hover:text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
                >
                  {viewLabel} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
